/**
 * Dashboard module card (#92): one card per registry entry.
 * Icon, label, one-line description, and a status line (or a
 * "coming soon" badge for a disabled module).
 */

import { escapeHtml } from '../core/utilities/dom';
import { logger } from '../core/logger';
import { ARROW_ICON, MODULE_ICONS } from '../web/shell/icons';
import { defaultPath } from '../web/shell/resolve-route';
import type { AppModule } from '../web/shell/module.types';

/** A module's status line; null when disabled, absent, empty or failing. */
export function readStatus(module: AppModule): string | null {
  if (!module.enabled || !module.getStatus) return null;
  try {
    return module.getStatus() || null;
  } catch (error) {
    logger.warn(`Status for module "${module.id}" failed:`, error);
    return null;
  }
}

export function renderModuleCard(
  module: AppModule,
  t: (key: string) => string
): string {
  const icon = `<span class="module-card__icon">${MODULE_ICONS[module.icon]}</span>`;
  const title = `<span class="module-card__title">${escapeHtml(t(module.labelKey))}</span>`;
  const description = `<span class="module-card__description">${escapeHtml(t(module.descriptionKey))}</span>`;

  if (!module.enabled) {
    return `<div class="module-card module-card--disabled" data-module="${module.id}" aria-disabled="true">
      <span class="module-card__top">${icon}<span class="module-card__badge">${escapeHtml(t('modules.comingSoon'))}</span></span>
      ${title}${description}
    </div>`;
  }

  const status = readStatus(module);
  return `<a class="module-card" data-module="${module.id}" href="#${defaultPath(module)}">
      <span class="module-card__top">${icon}</span>
      ${title}${description}
      <span class="module-card__footer">
        <span class="module-card__status">${status ? escapeHtml(status) : ''}</span>
        <span class="module-card__arrow">${ARROW_ICON}</span>
      </span>
    </a>`;
}
